import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { FaSearch, FaTruck, FaPercentage, FaLayerGroup, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
    fetchRetailerDistributors,
    fetchRetailerDistributorStats,
    fetchAvailableDistributors,
} from '../../../services/Http/Api';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';

const RetailerDistributors = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState('connected');

    // Stats state
    const [stats, setStats] = useState({
        total_distributors: 0,
        total_active_discounts: 0,
        categories_covered: 0,
    });
    const [statsLoading, setStatsLoading] = useState(true);

    // Connected distributors state
    const [distributors, setDistributors] = useState([]);
    const [distributorsLoading, setDistributorsLoading] = useState(true);
    const [distributorsPagination, setDistributorsPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
    });
    const [distributorsSearch, setDistributorsSearch] = useState('');
    const [debouncedDistributorsSearch, setDebouncedDistributorsSearch] = useState('');

    // Available distributors state
    const [availableDistributors, setAvailableDistributors] = useState([]);
    const [availableLoading, setAvailableLoading] = useState(true);
    const [availablePagination, setAvailablePagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
    });
    const [availableSearch, setAvailableSearch] = useState('');
    const [debouncedAvailableSearch, setDebouncedAvailableSearch] = useState('');

    // Debounce search for connected distributors
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDistributorsSearch(distributorsSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [distributorsSearch]);

    // Debounce search for available distributors
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAvailableSearch(availableSearch);
        }, 300);
        return () => clearTimeout(timer);
    }, [availableSearch]);

    // Load stats
    useEffect(() => {
        const loadStats = async () => {
            setStatsLoading(true);
            try {
                const data = await fetchRetailerDistributorStats();
                setStats(data || {});
            } catch (error) {
                console.error('Error loading stats:', error);
            } finally {
                setStatsLoading(false);
            }
        };
        loadStats();
    }, []);

    // Load connected distributors
    const loadDistributors = useCallback(async (page = 1) => {
        setDistributorsLoading(true);
        try {
            const params = {
                page,
                per_page: distributorsPagination.per_page,
            };
            if (debouncedDistributorsSearch) {
                params.search = debouncedDistributorsSearch;
            }
            const response = await fetchRetailerDistributors(params);
            setDistributors(response.data || []);
            setDistributorsPagination(response.pagination || distributorsPagination);
        } catch (error) {
            toast.error(t('RetailerDistributors.error'));
        } finally {
            setDistributorsLoading(false);
        }
    }, [debouncedDistributorsSearch, distributorsPagination.per_page, t]);

    // Load available distributors
    const loadAvailableDistributors = useCallback(async (page = 1) => {
        setAvailableLoading(true);
        try {
            const params = {
                page,
                per_page: availablePagination.per_page,
            };
            if (debouncedAvailableSearch) {
                params.search = debouncedAvailableSearch;
            }
            const response = await fetchAvailableDistributors(params);
            setAvailableDistributors(response.data || []);
            setAvailablePagination(response.pagination || availablePagination);
        } catch (error) {
            toast.error(t('RetailerDistributors.errorAvailable'));
        } finally {
            setAvailableLoading(false);
        }
    }, [debouncedAvailableSearch, availablePagination.per_page, t]);

    // Reload when search changes
    useEffect(() => {
        if (activeTab === 'connected') {
            loadDistributors(1);
        }
    }, [debouncedDistributorsSearch, loadDistributors, activeTab]);

    useEffect(() => {
        if (activeTab === 'available') {
            loadAvailableDistributors(1);
        }
    }, [debouncedAvailableSearch, loadAvailableDistributors, activeTab]);

    const handleDistributorsPageChange = (page) => {
        loadDistributors(page);
    };

    const handleAvailablePageChange = (page) => {
        loadAvailableDistributors(page);
    };

    const handleViewDistributor = (distributorId) => {
        navigate(`/distributors/${distributorId}`);
    };

    const connectedColumns = useMemo(() => [
        {
            name: t('RetailerDistributors.table.name'),
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <DistributorName>
                    {row.logo ? (
                        <Logo src={row.logo} alt={row.name} />
                    ) : (
                        <LogoPlaceholder>{row.name.charAt(0)}</LogoPlaceholder>
                    )}
                    <span>{row.name}</span>
                </DistributorName>
            ),
        },
        {
            name: t('RetailerDistributors.table.manufacturers'),
            selector: (row) => row.manufacturers_count,
            sortable: true,
            center: true,
            cell: (row) => <CountBadge>{row.manufacturers_count}</CountBadge>,
        },
        {
            name: t('RetailerDistributors.table.discounts'),
            selector: (row) => row.active_discounts_count,
            sortable: true,
            center: true,
            cell: (row) => (
                <DiscountBadge $hasDiscounts={row.active_discounts_count > 0}>
                    {row.active_discounts_count}
                </DiscountBadge>
            ),
        },
        {
            name: t('RetailerDistributors.table.categories'),
            selector: (row) => row.categories,
            cell: (row) => (
                <CategoriesCell>{row.categories || '-'}</CategoriesCell>
            ),
        },
        {
            name: t('RetailerDistributors.table.view'),
            center: true,
            width: '80px',
            cell: (row) => (
                <ViewButton onClick={() => handleViewDistributor(row.id)}>
                    <FaEye size={16} />
                </ViewButton>
            ),
        },
    ], [t, handleViewDistributor]);

    const availableColumns = useMemo(() => [
        {
            name: t('RetailerDistributors.table.name'),
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <DistributorName>
                    {row.logo ? (
                        <Logo src={row.logo} alt={row.name} />
                    ) : (
                        <LogoPlaceholder>{row.name.charAt(0)}</LogoPlaceholder>
                    )}
                    <span>{row.name}</span>
                </DistributorName>
            ),
        },
        {
            name: t('RetailerDistributors.table.manufacturers'),
            selector: (row) => row.manufacturers_count,
            sortable: true,
            center: true,
            cell: (row) => <CountBadge>{row.manufacturers_count}</CountBadge>,
        },
        {
            name: t('RetailerDistributors.table.categories'),
            selector: (row) => row.categories,
            cell: (row) => (
                <CategoriesCell>{row.categories || '-'}</CategoriesCell>
            ),
        },
        {
            name: t('RetailerDistributors.table.description'),
            selector: (row) => row.description,
            cell: (row) => (
                <DescriptionCell>{truncate(row.description, 60) || '-'}</DescriptionCell>
            ),
        },
    ], [t]);

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 600,
                fontSize: '14px',
                color: '#555',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
            },
        },
    };

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{t('RetailerDistributors.title')}</PageTitle>
                    <Subtitle>{t('RetailerDistributors.subtitle')}</Subtitle>
                </div>
            </PageHeader>

            <PageContent>
                {/* Stats Cards */}
                <StatsGrid>
                    <StatCard>
                        <StatIcon>
                            <FaTruck size={24} />
                        </StatIcon>
                        <StatContent>
                            <StatValue>{statsLoading ? '-' : stats.total_distributors}</StatValue>
                            <StatLabel>{t('RetailerDistributors.stats.connected')}</StatLabel>
                        </StatContent>
                    </StatCard>
                    <StatCard>
                        <StatIcon>
                            <FaPercentage size={24} />
                        </StatIcon>
                        <StatContent>
                            <StatValue>{statsLoading ? '-' : stats.total_active_discounts}</StatValue>
                            <StatLabel>{t('RetailerDistributors.stats.activeDiscounts')}</StatLabel>
                        </StatContent>
                    </StatCard>
                    <StatCard>
                        <StatIcon>
                            <FaLayerGroup size={24} />
                        </StatIcon>
                        <StatContent>
                            <StatValue>{statsLoading ? '-' : stats.categories_covered}</StatValue>
                            <StatLabel>{t('RetailerDistributors.stats.categories')}</StatLabel>
                        </StatContent>
                    </StatCard>
                </StatsGrid>

                {/* Tabs */}
                <TabsContainer>
                    <TabButton
                        $active={activeTab === 'connected'}
                        onClick={() => setActiveTab('connected')}
                    >
                        {t('RetailerDistributors.myDistributors')}
                    </TabButton>
                    <TabButton
                        $active={activeTab === 'available'}
                        onClick={() => setActiveTab('available')}
                    >
                        {t('RetailerDistributors.findNew')}
                    </TabButton>
                </TabsContainer>

                {/* Content */}
                {activeTab === 'connected' ? (
                    <>
                        <SearchWrapper>
                            <SearchIcon>
                                <FaSearch size={14} />
                            </SearchIcon>
                            <SearchInput
                                type="text"
                                placeholder={t('RetailerDistributors.searchPlaceholder')}
                                value={distributorsSearch}
                                onChange={(e) => setDistributorsSearch(e.target.value)}
                            />
                        </SearchWrapper>

                        <DataTable
                            columns={connectedColumns}
                            data={distributors}
                            pagination
                            paginationServer
                            paginationTotalRows={distributorsPagination.total}
                            paginationPerPage={distributorsPagination.per_page}
                            onChangePage={handleDistributorsPageChange}
                            progressPending={distributorsLoading}
                            highlightOnHover
                            customStyles={customStyles}
                            noDataComponent={<Placeholder>{t('RetailerDistributors.empty')}</Placeholder>}
                        />
                    </>
                ) : (
                    <>
                        <SearchWrapper>
                            <SearchIcon>
                                <FaSearch size={14} />
                            </SearchIcon>
                            <SearchInput
                                type="text"
                                placeholder={t('RetailerDistributors.searchAvailablePlaceholder')}
                                value={availableSearch}
                                onChange={(e) => setAvailableSearch(e.target.value)}
                            />
                        </SearchWrapper>

                        <InfoBanner>
                            {t('RetailerDistributors.availableInfo')}
                        </InfoBanner>

                        <DataTable
                            columns={availableColumns}
                            data={availableDistributors}
                            pagination
                            paginationServer
                            paginationTotalRows={availablePagination.total}
                            paginationPerPage={availablePagination.per_page}
                            onChangePage={handleAvailablePageChange}
                            progressPending={availableLoading}
                            highlightOnHover
                            customStyles={customStyles}
                            noDataComponent={<Placeholder>{t('RetailerDistributors.emptyAvailable')}</Placeholder>}
                        />
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default RetailerDistributors;

const truncate = (value, maxLength = 80) => {
    if (!value) return '';
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const Subtitle = styled.p`
    margin: 0;
    color: #777;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem;
    background: #fff;
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(130, 22, 146, 0.08);
`;

const StatIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StatContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatValue = styled.span`
    font-size: 1.75rem;
    font-weight: 700;
    color: rgba(130, 22, 146, 1);
`;

const StatLabel = styled.span`
    font-size: 0.875rem;
    color: #666;
`;

const TabsContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
`;

const TabButton = styled.button`
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : 'rgba(130, 22, 146, 0.08)')};
    color: ${({ $active }) => ($active ? '#fff' : 'rgba(130, 22, 146, 1)')};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 0.9)' : 'rgba(130, 22, 146, 0.15)')};
    }
`;

const SearchWrapper = styled.div`
    position: relative;
    max-width: 400px;
    margin-bottom: 1rem;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #888;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 0.625rem 0.875rem 0.625rem 2.375rem;
    border: 1px solid rgba(130, 22, 146, 0.3);
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;

    &:focus {
        border-color: rgba(130, 22, 146, 0.6);
        box-shadow: 0 0 0 2px rgba(130, 22, 146, 0.1);
    }
`;

const DistributorName = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 500;
`;

const Logo = styled.img`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: cover;
`;

const LogoPlaceholder = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
`;

const CountBadge = styled.span`
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.875rem;
`;

const DiscountBadge = styled.span`
    background: ${({ $hasDiscounts }) => ($hasDiscounts ? 'rgba(76, 175, 80, 0.15)' : 'rgba(158, 158, 158, 0.15)')};
    color: ${({ $hasDiscounts }) => ($hasDiscounts ? '#2e7d32' : '#616161')};
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.875rem;
`;

const CategoriesCell = styled.span`
    color: #666;
    font-size: 0.875rem;
`;

const DescriptionCell = styled.span`
    color: #888;
    font-size: 0.875rem;
`;

const ViewButton = styled.button`
    background: rgba(130, 22, 146, 0.1);
    border: none;
    color: rgba(130, 22, 146, 1);
    width: 36px;
    height: 36px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;

    &:hover {
        background: rgba(130, 22, 146, 0.2);
    }
`;

const InfoBanner = styled.div`
    background: rgba(130, 22, 146, 0.06);
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 8px;
    padding: 0.75rem 1rem;
    color: #555;
    font-size: 0.875rem;
    margin-bottom: 1rem;
`;

const Placeholder = styled.div`
    padding: 2.5rem;
    text-align: center;
    border: 1px dashed rgba(130, 22, 146, 0.3);
    border-radius: 12px;
    color: #777;
`;
